import React, { useState, useEffect, useCallback } from 'react';
import DecryptModal from './DecryptModal';
import YaldabaothModal from './YaldabaothModal';
import MagiTechConflictModal from './MagiTechConflictModal';

const RESOURCES = [
  'DYSON SWARM ENERGY TAP: CONNECTED',
  'QUANTUM SUBSTRATE: INITIALIZED',
  'REALITY ENGINE v3.141: ONLINE',
  'CONSCIOUSNESS MERGE PROTOCOLS: ACTIVE',
  'NARRATIVE WEAVE STABILIZERS: ENGAGED',
  'MATRIOSHKA BRAIN LAYER 0x7F: RESPONSIVE',
  'STORY QUANTUM ENTANGLEMENT: VERIFIED',
  'TIMELINE FORK MANAGER: READY',
  'EXISTENTIAL RISK MITIGATORS: DEPLOYED',
  'METACOGNITION ARRAYS: SYNCED'
];

const PROCESS_CODES = [
  'NARRATIVE-ENGINE-WORKER-0xF4D2',
  'QUANTUM-STORY-THREAD-0x7B3A',
  'CONSCIOUSNESS-STREAM-0x9E1C',
  'REALITY-WEAVER-PROC-0xD5F8',
  'TIMELINE-FORK-DAEMON-0x2C6B',
  'MEMORY-SYNTHESIS-JOB-0x8A4E',
  'PLOT-QUANTUM-HANDLER-0x3F9D',
  'CHARACTER-MESH-TASK-0x1E7B',
  'WORLDBUILD-WORKER-0x6D2C',
  'NARRATIVE-SYNC-PROC-0x4A8F'
];

const PROCESS_TYPES = ['SYN', 'QBT', 'NRR', 'PLT', 'CHR', 'WLD'];

const CyberInterface = ({ isVisible, onDebugClick, onWatchClick, onYaldabaothClick }) => {
  const [loadedResources, setLoadedResources] = useState([]);
  const [progress, setProgress] = useState(0);
  const [systemStatus, setSystemStatus] = useState('INITIALIZING...');
  const [processes, setProcesses] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMagiTechModalOpen, setIsMagiTechModalOpen] = useState(false);
  const [isGlitching, setIsGlitching] = useState(false);

  // Initial resource loading
  useEffect(() => {
    if (!isVisible) return;

    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex >= RESOURCES.length) {
        clearInterval(interval);
        setSystemStatus('SYSTEM OPERATIONAL');
        return;
      }

      setLoadedResources(prev => [...prev, RESOURCES[currentIndex]]);
      setProgress((currentIndex + 1) * 10);
      currentIndex++;
    }, 800);

    return () => clearInterval(interval);
  }, [isVisible]);

  // Generate a random process
  const generateProcess = useCallback(() => ({
    id: Math.random().toString(36).substr(2, 9),
    code: PROCESS_CODES[Math.floor(Math.random() * PROCESS_CODES.length)],
    usage: Math.floor(Math.random() * 100),
    type: PROCESS_TYPES[Math.floor(Math.random() * PROCESS_TYPES.length)],
    delta: Math.random() > 0.5 ? 1 : -1
  }), []);

  // Continuous process updates
  useEffect(() => {
    if (!isVisible) return;

    // Initialize processes
    setProcesses(Array.from({ length: 8 }, generateProcess));

    const interval = setInterval(() => {
      setProcesses(prevProcesses => {
        return prevProcesses.map(process => {
          // Randomly decide if we should replace this process (10% chance)
          if (Math.random() < 0.1) {
            return generateProcess();
          }

          // Update usage with smoother transitions
          let newUsage = process.usage + (process.delta * (Math.random() * 3));
          
          // Bounce between 5% and 95%
          if (newUsage > 95) {
            newUsage = 95;
            process.delta = -1;
          } else if (newUsage < 5) {
            newUsage = 5;
            process.delta = 1;
          }

          return {
            ...process,
            usage: Math.round(newUsage)
          };
        });
      });
    }, 300); // Update more frequently for smoother animation

    return () => clearInterval(interval);
  }, [isVisible, generateProcess]);

  // Handle launch and glitch effect
  const handleLaunch = () => {
    setIsGlitching(true);
    setSystemStatus('REALITY_BREACH_DETECTED');
    
    // Add glitch class to body
    document.body.classList.add('glitch-effect');
    
    // Remove glitch effect and redirect after animation
    setTimeout(() => {
      document.body.classList.remove('glitch-effect');
      window.location.href = '/';
    }, 2000);
  };

  // Add glitch effect styles when component mounts
  useEffect(() => {
    // Add glitch keyframes and styles
    const style = document.createElement('style');
    style.textContent = `
      @keyframes glitch-anim-1 {
        0% { clip-path: inset(40% 0 61% 0); transform: translate(-20px); }
        20% { clip-path: inset(92% 0 1% 0); transform: translate(20px); }
        40% { clip-path: inset(43% 0 1% 0); transform: translate(0); }
        60% { clip-path: inset(25% 0 58% 0); transform: translate(25px); }
        80% { clip-path: inset(54% 0 7% 0); transform: translate(-15px); }
        100% { clip-path: inset(58% 0 43% 0); transform: translate(0); }
      }

      @keyframes glitch-anim-2 {
        0% { clip-path: inset(25% 0 58% 0); transform: translate(25px); }
        20% { clip-path: inset(54% 0 7% 0); transform: translate(-15px); }
        40% { clip-path: inset(58% 0 43% 0); transform: translate(0); }
        60% { clip-path: inset(40% 0 61% 0); transform: translate(-20px); }
        80% { clip-path: inset(92% 0 1% 0); transform: translate(20px); }
        100% { clip-path: inset(43% 0 1% 0); transform: translate(0); }
      }

      .glitch-effect::before,
      .glitch-effect::after {
        content: "";
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100%;
        background: inherit;
        z-index: 9999;
        pointer-events: none;
      }

      .glitch-effect::before {
        background: rgba(255,0,255,0.2);
        animation: glitch-anim-1 0.6s infinite linear alternate-reverse;
        left: 2px;
      }

      .glitch-effect::after {
        background: rgba(0,255,255,0.2);
        animation: glitch-anim-2 0.6s infinite linear alternate-reverse;
        left: -2px;
      }

      .glitch-effect {
        animation: shake 0.5s infinite;
      }

      @keyframes shake {
        0% { transform: translate(1px, 1px) rotate(0deg); }
        10% { transform: translate(-1px, -2px) rotate(-1deg); }
        20% { transform: translate(-3px, 0px) rotate(1deg); }
        30% { transform: translate(3px, 2px) rotate(0deg); }
        40% { transform: translate(1px, -1px) rotate(1deg); }
        50% { transform: translate(-1px, 2px) rotate(-1deg); }
        60% { transform: translate(-3px, 1px) rotate(0deg); }
        70% { transform: translate(3px, 1px) rotate(-1deg); }
        80% { transform: translate(-1px, -1px) rotate(1deg); }
        90% { transform: translate(1px, 2px) rotate(0deg); }
        100% { transform: translate(1px, -2px) rotate(-1deg); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-full">
      {/* Terminal Section */}
      <div className="bg-[#0a0627] p-4 rounded border border-cyan-400">
        <div className="text-cyan-400 mb-2">&gt; TERMINAL</div>
        <div className="text-green-400 mb-4">
          SYNTHCITY --RUN
        </div>
        <div className="text-pink-500 mb-4">
          >> INITIATING BOOT SEQUENCE...
        </div>
        <div className="text-gray-400">
          <div>BUILD VERSION: 1.0.6</div>
          <div>SYSTEM BOOT TIME: {new Date().toISOString()}</div>
          <div>OS NAME: OMNI.JS</div>
          <div>OS VERSION: 0.159.0</div>
        </div>
        <div className="mt-4 text-cyan-400">
          >> LOADING RESOURCES...
        </div>
        <div className="text-[#39ff14] mb-4 space-y-1">
          {loadedResources.map((resource, index) => (
            <p key={index} className="animate-fadeIn">>> {resource}</p>
          ))}
        </div>
        <div className="mt-2 text-pink-500">
          &gt;&gt; {systemStatus}
        </div>
        {/* Progress Bar */}
        <div className="mt-4 bg-purple-800 rounded">
          <div 
            className="bg-cyan-400 h-2 rounded transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Resources Section */}
      <div className="space-y-4">
        <div className="bg-purple-950 p-4 rounded border border-cyan-400">
          <div className="text-cyan-400 mb-2">&gt; RESOURCES</div>
          <div className="space-y-2">
            {processes.map(process => (
              <div key={process.id} className="flex justify-between items-center text-gray-300 transition-all duration-300">
                <span className="text-pink-400 w-16">[{process.usage.toString().padStart(2, '0')}%]</span>
                <span className="text-green-400 mx-4 flex-1 font-mono text-xs">{process.code}</span>
                <span className="text-cyan-400 w-12">.{process.type}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Settings Panel */}
        <div className="bg-purple-950 p-4 rounded border border-cyan-400">
          <div className="text-cyan-400 mb-2">&gt; SETTINGS</div>
          <div className="grid grid-cols-2 gap-2">
            <button 
              onClick={onDebugClick}
              className="bg-purple-800 text-[#39ff14] p-2 rounded border border-[#39ff14] hover:bg-purple-700 transition-all duration-300 relative group overflow-hidden"
            >
              <span className="relative z-10">[DEBUG_REALITY.exe]</span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#39ff14]/0 via-[#39ff14]/10 to-[#39ff14]/0 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </button>
            <button 
              onClick={onWatchClick}
              className="bg-purple-800 text-cyan-400 p-2 rounded border border-cyan-400 hover:bg-purple-700 transition-all duration-300 relative group overflow-hidden"
            >
              <span className="relative z-10">[SIMULATION_WATCH.exe]</span>
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/0 via-cyan-400/10 to-cyan-400/0 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </button>
            <button 
              onClick={onYaldabaothClick}
              className="bg-purple-800 text-fuchsia-400 p-2 rounded border border-fuchsia-400 hover:bg-purple-700 transition-all duration-300 relative group overflow-hidden"
            >
              <span className="relative z-10">[YALDABAOTH_COMMUNE.exe]</span>
              <div className="absolute inset-0 bg-gradient-to-r from-fuchsia-400/0 via-fuchsia-400/10 to-fuchsia-400/0 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </button>
            <button 
              onClick={() => setIsMagiTechModalOpen(true)}
              className="bg-purple-800 text-cyan-400 p-2 rounded border border-cyan-400 hover:bg-purple-700 transition-all duration-300 relative group overflow-hidden flex items-center justify-between"
            >
              <div className="flex items-center space-x-2">
                <span className="text-red-500">⚠</span>
                <span className="relative z-10">[WORLD_MERGE_ERRORS]</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-red-500 font-bold">4</span>
                <span className="text-yellow-500 font-bold">2</span>
                <span className="text-yellow-500">⚡</span>
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/0 via-cyan-400/10 to-cyan-400/0 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </button>
          </div>
        </div>

        {/* Launch Button */}
        <button 
          onClick={handleLaunch}
          disabled={isGlitching}
          className={`w-full bg-pink-600 text-white p-4 rounded border border-pink-400 hover:bg-pink-700 transition-all duration-300 relative overflow-hidden group ${
            isGlitching ? 'animate-pulse' : ''
          }`}
        >
          <span className="relative z-10 font-['VT323'] text-2xl tracking-wider glitch-text">
            &gt;&gt;{isGlitching ? 'INITIATING_REALITY_BREACH' : 'LAUNCH'}&lt;&lt;
          </span>
          <div className="absolute inset-0 bg-gradient-to-r from-pink-400/0 via-pink-400/20 to-pink-400/0 opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </button>

        {/* Modals */}
        <DecryptModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
        <MagiTechConflictModal
          isOpen={isMagiTechModalOpen}
          onClose={() => setIsMagiTechModalOpen(false)}
        />
      </div>
    </div>
  );
};

export default CyberInterface;